import {Component, Inject, OnInit, AfterViewInit, ViewChild} from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef
} from '@angular/material/legacy-dialog';
import {TranslateService} from '@ngx-translate/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ViewFault} from '@docma-mm-core/fault/fault';
import {Project} from '@docma-mm-core/project/project';
import {AppContainerService} from '@mydocma/app-container';
import {FaultDuplicateWizardStepperService} from './services/fault-duplicate-wizard-stepper.service';
import {FaultDuplicateWizardViewService} from './services/fault-duplicate-wizard.view.service';
import {StepperService} from '@mydocma/import-export';
import {FaultDuplicateStepperComponent} from './stepper/fault-duplicate-stepper.component';
import {getIosOsMajorVersion, getSafariMajorVersion, isIosDevice} from '@docma-mm-core/utils/utils';

export interface IFaultDuplicateDialogData {
  fault: ViewFault;
  project: Project;
}

@UntilDestroy()
@Component({
  selector: 'mydocma-mm-fault-duplicate-wizard',
  templateUrl: './fault-duplicate-wizard.component.html',
  styleUrls: ['./fault-duplicate-wizard.component.scss'],
  providers: []
})
export class FaultDuplicateWizardComponent implements OnInit, AfterViewInit {

  constructor(@Inject(MAT_DIALOG_DATA) public dialogData: IFaultDuplicateDialogData,
              private dialogRef: MatDialogRef<FaultDuplicateWizardComponent>,
              private translateService: TranslateService,
              public appContainerService: AppContainerService,
              private stepperDataService: FaultDuplicateWizardStepperService,
              private viewService: FaultDuplicateWizardViewService) {

    this.engineMajorVersion = getSafariMajorVersion();
    this.iosOsMajorVersion = getIosOsMajorVersion();
    this.isAnimationUnsupportedDevice = this.computeIsAnimationUnsupportedDevice();
  }

  private static readonly ANIMATION_UNSUPPORTED_ENGINE_VERSIONS: number[] = [26];
  @ViewChild(FaultDuplicateStepperComponent) stepperComponent: FaultDuplicateStepperComponent;

  public isAnimationUnsupportedDevice: boolean;
  public readonly engineMajorVersion: number | null;
  public iosOsMajorVersion: number | null;

  stepperService: StepperService;
  currentStepIndex = 0;
  isLoading = false;
  canProceedToNext = false;

  ngOnInit(): void {
    this.stepperDataService.initialize(this.dialogData.fault, this.dialogData.project);

    this.stepperDataService.stepperData$.pipe(untilDestroyed(this)).subscribe(() => {
      setTimeout(() => this.updateValidationState());
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.stepperComponent) {
        this.stepperComponent.stepperService$.pipe(untilDestroyed(this)).subscribe(service => {
          if (service) {
            this.stepperService = service;
            this.stepperService.currentStep$.pipe(untilDestroyed(this)).subscribe(step => {
              if (step) {
                this.currentStepIndex = step.index;
                setTimeout(() => this.updateValidationState());
              }
            });
          }
        });
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close({ shouldExecute: false });
  }

  async onNext(): Promise<void> {
    if (this.currentStepIndex < 2) {
      if (this.stepperService) {
        await this.stepperService.nextStep();
      }
    } else {
      await this.onFinish();
    }
  }

  async onPrevious(): Promise<void> {
    if (this.currentStepIndex > 0) {
      if (this.stepperService) {
        await this.stepperService.prevStep();
      }
    }
  }

  async onFinish(): Promise<void> {
    const validationResult = await this.viewService.validateDuplicationState();
    if (!validationResult.isValid) {
      this.viewService.showErrorNotification(validationResult.errorMessage);
      return;
    }

    this.dialogRef.close({
      shouldExecute: true
    });
  }

  getNextButtonText(): string {
    if (this.currentStepIndex === 2) {
      return this.translateService.instant('MM.fault.duplicate.button.finish');
    }
    return this.translateService.instant('MM.general.button.next');
  }

  canProceed(): boolean {
    return this.canProceedToNext;
  }

  private updateValidationState(): void {
    switch (this.currentStepIndex) {
      case 0:
        this.canProceedToNext = this.stepperDataService.isOptionsStepValid();
        break;
      case 1:
        this.canProceedToNext = this.stepperDataService.isLocationStepValid();
        break;
      case 2:
        this.canProceedToNext = this.stepperDataService.isAttachmentStepValid();
        break;
      default:
        this.canProceedToNext = false;
    }
  }

  private computeIsAnimationUnsupportedDevice(): boolean {
    if (!isIosDevice()) {
      return false;
    }
    const versionToCheck = this.iosOsMajorVersion ?? this.engineMajorVersion;
    if (versionToCheck == null) {
      return false;
    }
    return FaultDuplicateWizardComponent.ANIMATION_UNSUPPORTED_ENGINE_VERSIONS.includes(versionToCheck);
  }
}
